import React from 'react';
import { Color } from 'modules/styles/colors';
import { Spacing, FontWeight, FontSize } from 'modules/styles/variables';
import { MuiBox } from 'theme/material-ui';
import FlexBox from 'components/UI/Layout/FlexBox';
import Typography from 'components/UI/Typography';
import PriorityHighRoundedIcon from '@material-ui/icons/PriorityHighRounded';
import { AlertCard, AlertTop, AlertSide, Dot, AlertDotBox } from './styled';

export function TransitionAlertCard({
  side = false,
  children
}: {
  iconColor?: string;
  side?: boolean;
  children: React.ReactNode;
}) {
  return (
    <AlertCard>
      {side ? <AlertSide /> : <AlertTop />}
      <MuiBox
        style={{ display: side ? undefined : 'flex' }}
        px={Spacing.xSmall}
        py={Spacing.xSmall}
      >
        <MuiBox
          style={{
            padding: '0px 10px',
            minWidth: side ? '185px' : 'initial',
            flex: 1,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <MuiBox
            style={{
              color: side ? Color.error : Color.primary,
              border: `2px solid ${side ? Color.error : Color.primary}`,
              borderRadius: '20px',
              display: 'inline-block',
              padding: '4px 4px 0px 4px',
              fontSize: '12px'
            }}
          >
            <PriorityHighRoundedIcon fontSize="small" />
          </MuiBox>
          {side ? (
            <Typography
              color={Color.primary}
              fontWeight={FontWeight.semibold}
              fontSize={FontSize.largeHeading}
              display="inline"
              style={{ paddingLeft: Spacing.medium }}
            >
              Important Notices
            </Typography>
          ) : null}
        </MuiBox>
        <FlexBox>{children}</FlexBox>
      </MuiBox>
    </AlertCard>
  );
}

export function HealthRecordsAlertCard({ onPress }: { onPress: () => void }) {
  return (
    <TransitionAlertCard>
      <Typography>
        Health records older than three years will not transfer to the new Intermountain Health app
        coming September 6. We recommend you download your health records before the transition to
        the new Intermountain Health app.
      </Typography>
      <MuiBox style={{ marginTop: Spacing.small }}>
        <div
          onClick={onPress}
          role="button"
          tabIndex={0}
          aria-label="Download Health Summary"
          style={{
            cursor: 'pointer',
            display: 'inline-block',
            outline: 'none'
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPress();
            }
          }}
        >
          <Typography color={Color.secondary} fontWeight={FontWeight.semibold}>
            Download Health Summary &gt;&gt;
          </Typography>
        </div>
      </MuiBox>
    </TransitionAlertCard>
  );
}

export function MessageTransitionPhase1Alert() {
  return (
    <TransitionAlertCard>
      <Typography color={Color.primary}>
        Download any messages you want to keep. These messages will not transfer to the new app
        coming September 6. Please note, if a message has an attached file, you must tap or click
        the attachment download arrow separately.
      </Typography>
    </TransitionAlertCard>
  );
}

export function MessageTransitionPhase2Alert() {
  return (
    <TransitionAlertCard side>
      <MuiBox py={Spacing.xSmall} px={Spacing.small}>
        <MuiBox>
          <Typography
            color={Color.primary}
            fontWeight="600"
            style={{ marginBottom: Spacing.small }}
          >
            In preparation for the new Intermountain Health app coming September 6 -
          </Typography>{' '}
        </MuiBox>
        <MuiBox style={{ marginBottom: Spacing.medium }}>
          <Typography color={Color.primary}>
            <AlertDot /> Creating and replying to messages is temporarily unavailable. If you have
            questions or need assistance, please call your provider's office directly.
          </Typography>
        </MuiBox>
        <MuiBox style={{ marginBottom: Spacing.medium }}>
          <Typography color={Color.primary}>
            <AlertDot /> Download any messages you want to keep. These messages will not transfer to
            the new app coming September 6. Please note, if a message has an attached file, you must
            tap or click the attachment download arrow separately.
          </Typography>
        </MuiBox>
      </MuiBox>
    </TransitionAlertCard>
  );
}

export const AlertDot = () => {
  return (
    <AlertDotBox>
      <Dot />
    </AlertDotBox>
  );
};
